// The playing file fill's file in the ready pool, mirrored as `window.video_file_element` and the videoFile slice (the D2 bridge for files).
import { setVideoSource } from '~/lib/videoFit'
import store from '~/store'
import { selectPlaylist } from '~/store/playback/selectors'
import { setDialog } from '~/store/ui'
import { clearFile, setDuration, setFile, setFps, setHasEnded, setMuted, setPlaying } from '~/store/videoFile'
import { fillPictures } from './fillSources'
import { onFileCatalog, startFileCatalog } from './files'
import { isFileSource } from './ids'
import { startPlayhead } from './playhead'
import * as pool from './videoPool'

// The pool node the transport plays: one file at a time, the first file fill in tree order.
export const NODE = 'transport'

// Said only for a file a fill on stage shows; a warm load fails quietly.
const fail = (id, message) => {
	if (!message) return
	const { live, camera } = store.getState()
	const shown = fillPictures(live.objects, { device: camera.device, bags: live.source_settings })
	if (shown.some(p => p.id === id)) store.dispatch(setDialog({ title: 'Video File', message }))
}

/** The slice follows the element; nothing else writes it while the file is on stage. Rate and volume are the playhead's (`currentPlaying`). */
const mirror = video => {
	const d = store.dispatch
	const size = () => {
		d(setDuration(Number.isFinite(video.duration) ? video.duration : null))
		if (video.videoWidth) setVideoSource(video.videoWidth, video.videoHeight)
	}
	const handlers = {
		loadedmetadata: () => {
			size()
			window.dispatchEvent(new CustomEvent('camera_stream_change'))
		},
		play: () => d(setPlaying(true)),
		pause: () => !video.ended && d(setPlaying(false)),
		ended: () => !video.loop && d(setHasEnded(true)),
	}
	size()
	d(setPlaying(!video.paused))
	if (video.ended && !video.loop) d(setHasEnded(true))
	d(setFps(video._fps ?? null))
	for (const [type, fn] of Object.entries(handlers)) video.addEventListener(type, fn)
	return () => {
		for (const [type, fn] of Object.entries(handlers)) video.removeEventListener(type, fn)
	}
}

let shown = null
let unmirror = null

// ⚠️ A file still loading keeps the previous one published, so a switch never blanks the transport or the fit.
const publish = () => {
	const entry = pool.stagedEntry(NODE)
	if (pool.stagedId(NODE) && !entry) return
	const video = entry?.video ?? null
	if (video === shown) return
	unmirror?.()
	unmirror = null
	shown = video
	window.video_file_element = video
	window.video_file_url = entry?.url ?? null
	window.dispatchEvent(new Event('video-element-change'))
	if (video) {
		store.dispatch(setFile({ fileName: entry.name, fileSize: entry.size }))
		store.dispatch(setMuted(pool.isMuted()))
		unmirror = mirror(video)
	} else {
		// ⚠️ Only if no camera has taken over: its acquisition is async and may already have published its size.
		if (!window.camera_stream) setVideoSource()
		store.dispatch(clearFile())
	}
	window.dispatchEvent(new CustomEvent('camera_stream_change'))
}

const ERRORS = {
	[globalThis.MediaError?.MEDIA_ERR_SRC_NOT_SUPPORTED ?? 4]: 'This video format is not supported by your browser.',
}

const onPool = (type, payload) => {
	if (type === 'stage' && payload.node === NODE) publish()
	else if (type === 'fps' && shown && pool.stagedId(NODE) === payload.id) store.dispatch(setFps(shown._fps ?? null))
	// A prompt-state handle after a reload needs a click on the file again; anything else is gone.
	else if (type === 'lost')
		fail(
			payload.id,
			payload.needsGesture ? null : 'Could not reopen the previous video file. Please select a file again.'
		)
	else if (type === 'error') fail(payload.id, ERRORS[payload.code] ?? 'Could not load the video file.')
}

/** The files of the presets around the current one, each at that preset's start cue, so a preset switch onto them has its frame at once. */
const upcomingFiles = state => {
	const presets = selectPlaylist(state)
	const { current_preset_index: cur, next_preset_index: next } = state.playback
	const here = next >= 0 ? next : cur
	const seen = new Set([pool.stagedId(NODE)])
	const list = []
	for (const i of [here + 1, cur - 1, here - 1]) {
		const settings = presets[i]?.settings
		if (!settings?.objects) continue
		const bags = settings.source_settings
		for (const { source, id } of fillPictures(settings.objects, { bags })) {
			if (!isFileSource(source) || !id || seen.has(id)) continue
			seen.add(id)
			list.push({ id, at: bags?.[source]?.start ?? 0 })
		}
	}
	return list
}

let lastWarm = ''
let lastInputs = []
const warmUpcoming = () => {
	const state = store.getState()
	const inputs = [
		selectPlaylist(state),
		state.playback.current_preset_index,
		state.playback.next_preset_index,
		pool.stagedId(NODE),
	]
	if (inputs.every((v, i) => v === lastInputs[i])) return
	lastInputs = inputs
	const list = upcomingFiles(state)
	const key = list.map(w => `${w.id}@${w.at}`).join('|')
	if (key === lastWarm) return
	lastWarm = key
	pool.warm(list)
}

let started = false
export const startFileStage = () => {
	if (started) return
	started = true
	startFileCatalog()
	pool.subscribe(onPool)
	startPlayhead(NODE)
	store.subscribe(warmUpcoming)
	onFileCatalog(() => {
		lastInputs = []
		warmUpcoming()
	})
}

/** Puts `id` (`file:<id>`, or null) on the transport. */
export const stageFile = id => {
	startFileStage()
	pool.stage(NODE, isFileSource(id) ? id : null)
}
